import { useState, useMemo } from 'react'
import {
  addMonths, subMonths, addWeeks, subWeeks, addDays, subDays,
  startOfMonth, endOfMonth, startOfWeek, endOfWeek, startOfDay, endOfDay, format,
} from 'date-fns'
import { useCalendarEvents } from './hooks/useCalendarEvents'
import { useCalendarVisibility } from './hooks/useCalendarVisibility'
import { useExpandedEvents } from './hooks/useExpandedEvents'
import { useCalendars } from './hooks/useCalendars'
import { useSystemCalendarEvents } from './hooks/useSystemCalendarEvents'
import { useExternalEvents } from './hooks/useExternalCalendars'
import { useMembers } from '../../hooks/useMembers'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../hooks/useAuth'
import { MonthGrid } from './components/MonthGrid'
import { WeekView } from './components/WeekView'
import { DayView } from './components/DayView'
import { DayPanel } from './components/DayPanel'
import { EventDetail } from './components/EventDetail'
import { EventForm } from './components/EventForm'
import { CalendarManager } from './components/CalendarManager'
import { SearchBar } from '../../components/ui/SearchBar'
import { Portal } from '../../components/Portal'

const useTasks = () => {
  const { household } = useAuth()
  return useQuery({
    queryKey: ['tasks-brief', household?.id],
    queryFn: async () => {
      if (!household?.id) return []
      const { data, error } = await supabase
        .from('tasks')
        .select('id, title, event_id, completed_at')
        .eq('household_id', household.id)
        .not('event_id', 'is', null)
      if (error) throw error
      return data ?? []
    },
    enabled: !!household?.id,
  })
}

export const CalendarPage = () => {
  const [view,        setView]        = useState('month')   // 'month' | 'week' | 'day'
  const [cursor,      setCursor]      = useState(new Date())
  const [selectedDay, setSelectedDay] = useState(null)
  const [activeEvent, setActiveEvent] = useState(null)
  const [editingEvent, setEditingEvent] = useState(null)    // null | event | 'new'
  const [showManager, setShowManager] = useState(false)
  const [search,      setSearch]      = useState('')

  const visibility = useCalendarVisibility()
  const { data: calendars = [] } = useCalendars()
  const { data: members = [] }   = useMembers()
  const { data: tasks = [] }     = useTasks()

  const rangeStart = useMemo(() => {
    if (view === 'month') return startOfWeek(startOfMonth(cursor)).toISOString()
    if (view === 'day')   return startOfDay(cursor).toISOString()
    return startOfWeek(cursor).toISOString()
  }, [view, cursor])
  const rangeEnd = useMemo(() => {
    if (view === 'month') return endOfWeek(endOfMonth(cursor)).toISOString()
    if (view === 'day')   return endOfDay(cursor).toISOString()
    return endOfWeek(cursor).toISOString()
  }, [view, cursor])

  const { data: eventsData, isLoading } = useCalendarEvents(rangeStart, rangeEnd)
  const calEvents      = useExpandedEvents(eventsData, rangeStart, rangeEnd, visibility.hiddenIds)
  const systemEvents   = useSystemCalendarEvents(rangeStart, rangeEnd, visibility.hiddenIds)
  const { data: extEvents = [] } = useExternalEvents(rangeStart, rangeEnd, visibility.hiddenIds)
  const events         = useMemo(() => [...calEvents, ...systemEvents, ...extEvents], [calEvents, systemEvents, extEvents])

  const isSearching = search.trim().length > 0
  const searchResults = useMemo(() => {
    if (!isSearching) return []
    const q = search.toLowerCase()
    return events.filter((e) =>
      e.title?.toLowerCase().includes(q) || e.description?.toLowerCase().includes(q)
    ).sort((a, b) => new Date(a.start_time) - new Date(b.start_time))
  }, [events, search, isSearching])

  const navPrev = () => {
    if (view === 'month') setCursor(subMonths(cursor, 1))
    else if (view === 'day') setCursor(subDays(cursor, 1))
    else setCursor(subWeeks(cursor, 1))
  }
  const navNext = () => {
    if (view === 'month') setCursor(addMonths(cursor, 1))
    else if (view === 'day') setCursor(addDays(cursor, 1))
    else setCursor(addWeeks(cursor, 1))
  }

  const handleDaySelect = (day) => {
    setSelectedDay(day)
    setActiveEvent(null)
  }

  const handleEventClick = (event) => {
    setActiveEvent(event)
    setSelectedDay(null)
  }

  const handleEdit = (event) => {
    setEditingEvent(event)
    setActiveEvent(null)
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header — two rows on mobile, one row on sm+ */}
      <div className="border-b border-border bg-surface flex-shrink-0">
        {/* Row 1: navigation */}
        <div className="flex items-center px-4 pt-2.5 pb-1">
          <button onClick={navPrev} className="p-1.5 rounded-lg hover:bg-background transition-colors flex-shrink-0">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="m15 18-6-6 6-6" />
            </svg>
          </button>
          <h1 className="flex-1 text-base font-bold text-text-primary text-center">
            {view === 'month' ? format(cursor, 'MMMM yyyy')
             : view === 'day' ? format(cursor, 'EEEE, MMM d')
             : `Week of ${format(startOfWeek(cursor), 'MMM d')}`}
          </h1>
          <button onClick={navNext} className="p-1.5 rounded-lg hover:bg-background transition-colors flex-shrink-0">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="m9 18 6-6-6-6" />
            </svg>
          </button>
        </div>

        {/* Row 2: controls */}
        <div className="flex items-center justify-end gap-2 px-4 pb-2">
          {/* View toggle */}
          <div className="flex rounded-lg border border-border overflow-hidden">
            {(['month', 'week', 'day']).map((v) => (
              <button
                key={v}
                onClick={() => setView(v)}
                className={`px-3 py-1 text-xs font-medium capitalize transition-colors ${
                  view === v ? 'bg-primary text-white' : 'text-text-muted hover:bg-background'
                }`}
              >
                {v}
              </button>
            ))}
          </div>

          {/* Calendar manager */}
          <button
            onClick={() => setShowManager(true)}
            className="p-1.5 rounded-lg hover:bg-background transition-colors text-text-muted"
            title="Manage calendars"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="4" width="18" height="18" rx="2" />
              <path d="M16 2v4M8 2v4M3 10h18" />
            </svg>
          </button>

          {/* New event */}
          <button
            onClick={() => { setEditingEvent('new'); setSelectedDay(null) }}
            className="flex items-center gap-1.5 bg-primary text-white text-sm font-medium px-3 py-1.5 rounded-xl hover:bg-primary/90 transition-colors"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M12 5v14M5 12h14" />
            </svg>
            <span className="hidden sm:inline">New</span>
          </button>
        </div>
      </div>

      {/* Search bar */}
      <div className="px-4 py-2 border-b border-border bg-surface flex-shrink-0">
        <SearchBar value={search} onChange={setSearch} placeholder="Search events…" />
      </div>

      {/* Calendar body */}
      {isLoading ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="w-8 h-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
        </div>
      ) : isSearching ? (
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
          {searchResults.length === 0 ? (
            <p className="text-center text-text-muted text-sm py-12">No events matching "{search}"</p>
          ) : searchResults.map((e) => (
            <button
              key={e._key ?? e.id}
              onClick={() => handleEventClick(e)}
              className="w-full text-left bg-surface border border-border rounded-xl px-4 py-3 hover:border-primary/30 transition-colors"
            >
              <p className="font-medium text-text-primary text-sm">{e.title}</p>
              <p className="text-xs text-text-muted mt-0.5">
                {format(new Date(e.start_time), 'EEE, MMM d · h:mm a')}
              </p>
              {e.description && (
                <p className="text-xs text-text-secondary mt-1 line-clamp-1">{e.description}</p>
              )}
            </button>
          ))}
        </div>
      ) : view === 'month' ? (
        <MonthGrid
          month={cursor}
          events={events}
          members={members}
          selectedDay={selectedDay}
          onDaySelect={handleDaySelect}
        />
      ) : view === 'day' ? (
        <DayView
          day={cursor}
          events={events}
          members={members}
          onEventClick={handleEventClick}
        />
      ) : (
        <WeekView
          week={cursor}
          events={events}
          onEventClick={handleEventClick}
          onDaySelect={(day) => { handleDaySelect(day); setView('day'); setCursor(day) }}
        />
      )}

      {/* Day panel */}
      {selectedDay && !activeEvent && !editingEvent && (
        <Portal>
          <DayPanel
            day={selectedDay}
            events={events}
            members={members}
            onEventClick={handleEventClick}
            onClose={() => setSelectedDay(null)}
          />
        </Portal>
      )}

      {/* Event detail */}
      {activeEvent && !editingEvent && (
        <Portal>
          <EventDetail
            event={activeEvent}
            members={members}
            tasks={tasks}
            onEdit={handleEdit}
            onClose={() => setActiveEvent(null)}
          />
        </Portal>
      )}

      {/* Event form */}
      {editingEvent && (
        <Portal>
          <EventForm
            event={editingEvent === 'new' ? null : editingEvent}
            calendars={calendars}
            members={members}
            initialDay={selectedDay ?? new Date()}
            onClose={() => setEditingEvent(null)}
            onSaved={() => {
              setEditingEvent(null)
              setSelectedDay(null)
              setActiveEvent(null)
            }}
          />
        </Portal>
      )}

      {/* Calendar manager */}
      {showManager && (
        <Portal>
          <CalendarManager onClose={() => setShowManager(false)} />
        </Portal>
      )}
    </div>
  )
}
